import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { SMLContainer } from '../../smartLinks/smlContainer/SMLContainer';
import { SMLPageHead } from '../components/SMLPageHead/SMLPageHead';
import { useGetAccountsQuery } from '../../../redux/api/fymAPI';
import { useAccountId, useUserToken } from '../../../redux/slices/appInfo/hooks';
import { smlNavigationMessages } from '../navigation/smlNavigationMessages';
import { ArtistHomeHeader } from './ArtistHomeHeader';
import { ArtistHomeContent } from './ArtistHomeContent';
import { ArtistHomeNoArtist } from './ArtistHomeNoArtist';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { useHomePeriod } from './useHomePeriod';
import { countOwnArtists } from '../../accountSelector/demoAccounts';

/**
 * The artist Home at `/app/home` (design: music24-artist-home a, b, c). It is about the active
 * artist, the one in the switcher. A workspace with no artist sees the "Add an artist" card
 * (design b1). The first-release checklist leads the Home of an artist (design: onboarding d1).
 */
const ArtistHomeViewComponent: React.FC = () => {
  const intl = useIntl();
  const userToken = useUserToken();
  const accountId = useAccountId();
  const period = useHomePeriod();

  const { accounts } = useGetAccountsQuery(undefined, {
    skip: !userToken,
    selectFromResult: ({ data }) => ({ accounts: data?.accounts }),
  });
  const account = useMemo(() => accounts?.find((item) => item.id === accountId), [accounts, accountId]);
  const breadcrumbs = useMemo(() => [{ name: intl.formatMessage(smlNavigationMessages.home) }], [intl]);

  // No artist of their own and no explicit pick of the demo artist: the "Add an artist" card.
  // LaziiBee stays one click away in the switcher.
  if (accounts !== undefined && account === undefined && countOwnArtists(accounts) === 0) {
    return (
      <SMLContainer header={<SMLPageHead breadcrumbs={breadcrumbs} title={intl.formatMessage(messages.pageTitle)} />}>
        <ArtistHomeNoArtist />
      </SMLContainer>
    );
  }

  if (account === undefined || accountId === null) {
    return <LoadingView />;
  }

  return (
    <SMLContainer header={<ArtistHomeHeader account={account} period={period} />}>
      <ArtistHomeContent key={account.id} accountId={account.id} days={period.days} />
    </SMLContainer>
  );
};

export const ArtistHomeView = TypedMemo(ArtistHomeViewComponent);
